import { customerService } from './customerService';
import creditService from './creditService';

const dashboardService = {
  // Centralized method to calculate credit customers data
  // This ensures consistency between dashboard and credit customers report
  getCreditCustomersData: async () => {
    try {
      // Option 1: Show only customers with positive balance (current behavior)
      // Option 2: Show all customers with credit history (new behavior)
      
      // Use Option 2 by default - show all customers with credit history
      const USE_CREDIT_HISTORY_APPROACH = true;
      
      if (USE_CREDIT_HISTORY_APPROACH) {
        console.log('📊 Using credit history approach - showing all customers with transaction history');
        
        // Get all customers with credit transaction history
        const allCustomersWithHistory = await creditService.getAllCustomersWithCreditHistory();
        
        console.log('Customers with credit transaction history:', allCustomersWithHistory.length);
        
        // Normalize data structure and calculate totals
        const customers = allCustomersWithHistory.map(customer => ({
          ...customer,
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          creditBalance: customer.creditBalance || 0,
          openingBalance: customer.openingBalance || 0,
          balanceDue: customer.balanceDue || 0,
          totalReturns: customer.totalReturns || 0,
          createdAt: customer.createdAt
        }));
        
        // Calculate totals
        const totalBalanceDue = customers.reduce((sum, customer) => sum + parseFloat(customer.balanceDue || 0), 0);
        const customersWithPositiveBalance = customers.filter(customer => parseFloat(customer.balanceDue) > 0);
        
        console.log('📊 Credit History Results:');
        console.log('   Total customers with history:', customers.length);
        console.log('   Customers with positive balance:', customersWithPositiveBalance.length);
        console.log('   Total balance due (all customers):', totalBalanceDue);
        
        return {
          customers: customers, // Show ALL customers with history
          totalCreditCustomers: customers.length, // Count all customers with history
          totalDueAmount: totalBalanceDue, // Sum of all balances (including zero/negative)
          customersWithPositiveBalance: customersWithPositiveBalance.length // Additional info
        };
      } else {
        // Original approach - only show customers with positive balance
        console.log('📊 Using positive balance approach - showing only customers with balance due > 0');
        
        // Fetch all customers
        const response = await customerService.getAllCustomers({
          page: 0,
          size: 1000, // Large enough to get all customers
          sort: 'name',
          direction: 'ASC'
        });
        
        // Filter customers with credit balance > 0 OR opening balance > 0 and normalize data structure
        const potentialCreditCustomers = response.content.map(customer => {
          // Normalize field names to ensure consistency
          return {
            ...customer,
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            email: customer.email,
            creditBalance: customer.creditBalance || customer.credit_balance || 0,
            openingBalance: customer.openingBalance || customer.opening_balance || 0,
            createdAt: customer.createdAt || customer.created_at
          };
        }).filter(customer => 
          parseFloat(customer.creditBalance) > 0 || parseFloat(customer.openingBalance) > 0
        );
        
        console.log('Potential credit customers (with credit_balance > 0 OR opening_balance > 0):', potentialCreditCustomers.length);
        
        // Fetch balance due for each customer to get accurate net balance
        const customersWithBalanceDue = await Promise.all(
          potentialCreditCustomers.map(async (customer) => {
            try {
              const creditSummary = await creditService.getCustomerCreditSummary(customer.id);
              return {
                ...customer,
                balanceDue: creditSummary.balanceDue || 0,
                totalReturns: creditSummary.totalReturns || 0
              };
            } catch (error) {
              console.error(`Error fetching balance due for customer ${customer.id}:`, error);
              // Fallback to current balance if API call fails
              return {
                ...customer,
                balanceDue: customer.creditBalance || customer.openingBalance || 0,
                totalReturns: 0
              };
            }
          })
        );
        
        // Filter out customers with zero balance due (after returns)
        const customersWithPositiveBalance = customersWithBalanceDue.filter(
          customer => parseFloat(customer.balanceDue) > 0
        );
        
        console.log('Customers with positive balance due after calculation:', customersWithPositiveBalance.length);
        
        // Calculate the total balance due (net amount after returns)
        const totalBalanceDue = customersWithPositiveBalance.reduce(
          (sum, customer) => sum + parseFloat(customer.balanceDue), 
          0
        );
        
        return {
          customers: customersWithPositiveBalance,
          totalCreditCustomers: customersWithPositiveBalance.length,
          totalDueAmount: totalBalanceDue
        };
      }
    } catch (error) {
      console.error('Error fetching credit customers data:', error);
      throw error;
    }
  }
};

export default dashboardService; 